import { autoinject, bindable, Container } from 'aurelia-framework';
import { ListFieldTyped } from 'models/ListSettings';
import {
  CommentForEntity,
  MooringFleetAnchorLine,
  MooringComponent,
  MooringComponentType,
  MooringOfferDto,
  MooringArticleSearchResult,
} from 'models/mooring';
import { MooringComponentSearchHandler, MooringOfferService, MooringService } from 'services/mooring';
import { MooringArticleService, ToastService } from 'services';
import { toKg, toM, toChecked } from 'lib/ui/ValueDisplayFormatters';
import { withNumber } from 'lib/helpers';
import { DialogService } from 'aurelia-dialog';
import { TableEntryManager } from 'services/ui-helpers';
import { MooringTableBase } from '../table-base';
import { PubSub } from 'lib/event/PubSub';
import { I18N } from 'aurelia-i18n';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { AsyncErrorHandler } from 'lib/ui';
import { TableManager } from '../TableManager';
import { cleanDmsFormat } from 'lib/helpers';
import { calculateFactorsMblAnchoring } from '../factors';
import { SearchResult } from 'elements/search/search-input';
import { MooringSelectEntriesForPupulateArgs } from 'features/mooring/component-templates/mooring-select-entries-for-populate';
import { MaterialFactor } from 'models/mooring/MaterialFactor';
import { MaterialFactorService } from 'services/material-factor-service';
import { OfferViewContext } from '../../OfferViewContext';
import { CalculateUtilizationFactorDto, MooringStep } from 'models/mooring/UtilizationFactor';
import { MooringComponentSearchResult } from 'services/mooring/mooring-component-search-handler';

@autoinject
export class FleetAnchorLinesTable extends MooringTableBase<MooringFleetAnchorLine> {
  protected table = new TableManager('fleet-anchor-lines-table', tableColumns);
  protected mooringOffer!: MooringOfferDto;
  protected context = OfferViewContext.useContext(this.container);

  @bindable
  protected onSave!: (args: { entries: MooringFleetAnchorLine[]; canNavigateOnSave: boolean }) => void;

  constructor(
    private container: Container,
    protected t: I18N,
    protected toast: ToastService,
    dialogService: DialogService,
    mooringArticleService: MooringArticleService,
    // Used in HTML for article search
    mooringComponentSearchHandler: MooringComponentSearchHandler,
    tableEntryManager: TableEntryManager,
    pubsub: PubSub,
    confirmation: ConfirmDialog,
    mooringOfferService: MooringOfferService,
    mooringService: MooringService,
    materialFactorService: MaterialFactorService,
    materialFactors: MaterialFactor
  ) {
    super(
      'fleet-anchor-line',
      dialogService,
      mooringComponentSearchHandler,
      tableEntryManager,
      mooringArticleService,
      pubsub,
      confirmation,
      mooringOfferService,
      mooringService,
      materialFactorService,
      materialFactors
    );

    pubsub.sub('tabs:change-guard', async () => {
      if (this.changeTracker.areEqual(this.entries)) {
        this.pubsub.publish('tabs:change-guard-ok', null);
        return;
      }

      const res = await this.confirmation.confirmSave();
      if (res == 'cancel') {
        return;
      } else if (res === 'discard-continue') {
        this.pubsub.publish('tabs:change-guard-ok', null);
      } else {
        this.onSave({ entries: this.entries, canNavigateOnSave: true });
      }
    });
  }

  async attached() {
    await super.attached();
    const { data: offer } = await this.mooringOfferService.getOffer(this.offerId);
    this.mooringOffer = offer;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onElementSelected(_: MooringComponentType, __: { id: number; title: string }, ___: MooringFleetAnchorLine) {}

  protected onArticleSelected(searchResult: MooringComponentSearchResult, component: MooringComponent, line: MooringFleetAnchorLine) {
    const res = searchResult.return;
    if (!res) return;
    component.ArticleProductId = res.Id;
    component.MBL = res.MBL;
    component.ProductName = res.ProductName;
    component.CategoryName = res.Category;
    component.TypeName = res.Type;
    component.ProductTypeName = res.ProductType;
    component.PrimaryDimension = res.PrimaryDimension;
    component.SecondaryDimension = res.SecondaryDimension;
    component.CategoryType = res.CategoryType;
    component.TypeVariant = res.TypeVariant;

    this.setComponentQuantity(component, line);

    this.onSave({
      entries: this.entries,
      canNavigateOnSave: false,
    });
  }

  protected getEntriesForPopulate(): MooringSelectEntriesForPupulateArgs {
    const entries = structuredClone(this.entries);
    return {
      groups: [
        {
          _identifier: 'fleet-anchor-lines',
          title: this.t.tr('mooring.fleetAnchorLine_plural'),
          options: entries.map((x) => ({
            name: x.AnalysisNo || '',
            id: x.Id,
            selected: false,
            articles: x.Components?.length || 0,
          })),
        },
      ],
    };
  }

  protected onAddComponent() {
    this.entries.push({
      AnalysisNo: ``,
    } as MooringFleetAnchorLine);
  }

  @AsyncErrorHandler
  protected async onDelete(index: number) {
    const res = await this.confirmation.confirmDelete();
    if (!res) return;

    const entry = this.entries[index];
    if (!entry) return;

    if (entry.Id) {
      await this.mooringService.deleteFleetAnchorLine(this.mooringId, entry.Id);
    }

    this.entries.splice(index, 1);
    this.toast.showDeleted();
  }

  protected save() {
    this.onSave({
      canNavigateOnSave: false,
      entries: this.entries,
    });
  }

  protected addEntryComment(component: MooringFleetAnchorLine) {
    this.openCommentDialog({
      mooringId: this.mooringId,
      isReadOnly: false,
      componentId: component.Id,
      componentType: CommentForEntity.FleetAnchorLine,
    });
  }

  protected addComponentComment(component: MooringComponent) {
    this.openCommentDialog({
      mooringId: this.mooringId,
      isReadOnly: false,
      componentId: component.Id,
      componentType: CommentForEntity.FleetAnchorLineComponent,
    });
  }

  protected async clearForm() {
    const approved = await this.confirmation.confirmYesCancel('mooring.clearForm', 'mooring.clearFormConfirmation');

    if (approved) {
      const restedTable = this.resetFields(this.entries, fieldsToReset);
      this.entries = restedTable;
    }
  }

  protected resetFields<T extends Record<string, any>>(anchorFleetLines: T[], fieldsToReset: (keyof T)[]): T[] {
    return anchorFleetLines.map((anchorFleetLines) => {
      const updatedAnchorFleetLines = { ...anchorFleetLines };

      fieldsToReset.forEach((field) => {
        const value = updatedAnchorFleetLines[field];

        if (field === 'LengthOfBottomRope' || field === 'LengthOfRope' || field === 'TotalLength') {
          (updatedAnchorFleetLines[field] as number) = 0;
        } else if (typeof value === 'boolean') {
          (updatedAnchorFleetLines[field] as boolean) = false;
        } else {
          updatedAnchorFleetLines[field] = null;
        }
      });

      return updatedAnchorFleetLines;
    });
  }

  protected onInputChange(index: number, name: (typeof tableColumns)[number]['field']) {
    const line = this.entries[index];

    if (name === 'LengthOfRope' || name === 'LengthOfBottomRope') {
      line.TotalLength = (+line.LengthOfBottomRope || 0) + (+line.LengthOfRope || 0);
      this.setComponentsQuantity(line.Components, line);
    }

    if (name === 'LengthOfRope' || name === 'LengthOfBottomRope' || name === 'DepthAtAnchoringPoint' || name === 'TotalLength') {
      const depth = withNumber(line.DepthAtAnchoringPoint).subtract(this.mooringOffer.SiteSetup.MooringDepth).value;
      line.Slope = withNumber(line.TotalLength).divideBy(depth).value;
    }

    if (name === 'StartPositionLat' || name === 'StartPositionLong' || name === 'StopPositionLat' || name === 'StopPositionLong') {
      line[name] = cleanDmsFormat(line[name]);
    }

    if (name === 'IsAccident' || name === 'DimForce') {
      calculateFactorsMblAnchoring(line.DimForce, line, this.materialFactors, line.IsAccident);
      this.calculateUtilizationFactor(line.Components, line);
    }
  }

  protected async calculateUtilizationFactor(component: MooringComponent[], line: MooringFleetAnchorLine) {
    const calculateUtilizationFactorDtos: CalculateUtilizationFactorDto[] = [];
    component.forEach((component) => {
      calculateUtilizationFactorDtos.push({
        Id: component.Id,
        HasKnot: component.HasKnot,
        MblRopes: line.MblRopes,
        MblSinkRopes: line.MblSinkRopes,
        MblStraps: line.MblStraps,
        MblChainShackleConnectionPlates: line.MblChainShackleConnectionPlates,
        MblUsedChainRopeWithKnot: line.MblUsedChainRopeWithKnot,
        MblAnchoring: line.MblAnchoring,
      });
    });

    const response = await this.mooringService.calculateUtilizationFactor({
      mooringId: this.mooringId,
      mooringStep: MooringStep.FLEET_ANCHOR_LINE,
      dto: calculateUtilizationFactorDtos,
    });

    response?.data.forEach((element) => {
      component.find((x) => x.Id === element.Id).UtilizationFactor = element.UtilizationFactor;
    });
  }

  protected setComponentQuantity(component: MooringComponent, line: MooringFleetAnchorLine) {
    if (component.CategoryName === 'Tau' || component.CategoryName === 'Rope') {
      component.Quantity = line.LengthOfRope;
    }
    if (component.CategoryName === 'Kjetting' || component.CategoryName === 'Chain') {
      component.Quantity = line.LengthOfBottomRope;
    }
  }

  protected setComponentsQuantity(components: MooringComponent[], line: MooringFleetAnchorLine) {
    components.forEach((compponent) => {
      if (compponent.CategoryName === 'Tau' || compponent.CategoryName === 'Rope') {
        compponent.Quantity = line.LengthOfRope;
      }
      if (compponent.CategoryName === 'Kjetting' || compponent.CategoryName === 'Chain') {
        compponent.Quantity = line.LengthOfBottomRope;
      }
    });
  }

  protected override postPasteAdjustment(index: number, name: (typeof tableColumns)[number]['field']) {
    this.onInputChange(index, name);
  }

  protected unbind() {
    this.pubsub.unsub();
    super.unbind();
  }

  protected detached() {
    this.pubsub.unsub();
  }
}

const fieldsToReset: (keyof MooringFleetAnchorLine)[] = [
  'DimForce',
  'IsAccident',
  'MblRopes',
  'MblSinkRopes',
  'MblStraps',
  'MblChainShackleConnectionPlates',
  'MblUsedChainRopeWithKnot',
  'MblAnchoring',
  'BottomAnchoringUplift',
  'DepthAtAnchoringPoint',
  'HoldingPowerAnchoring',
  'LengthOfBottomRope',
  'LengthOfRope',
  'TotalLength',
  'Slope',
  'StopPositionLat',
  'StopPositionLong',
];

const tableColumns: ListFieldTyped<MooringFleetAnchorLine>[] = [
  {
    field: 'AnalysisNo',
    title: 'mooring.analysisNo',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    inputType: 'text',
  },
  {
    field: 'CornerNo',
    title: 'general.corner',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    inputType: 'int',
  },
  {
    field: 'DimForce',
    title: 'mooring.dimForce',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'IsAccident',
    title: 'mooring.isAccident',
    helpText: 'mooring.dimForceDescription',
    selected: true,
    visible: true,
    filterType: 'BOOL',
    transform: toChecked,
    inputType: 'bool',
  },
  {
    field: 'MblRopes',
    title: 'mooring.minMblRope',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblSinkRopes',
    title: 'mooring.minMblSinkRope',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblStraps',
    title: 'mooring.minMblStrap',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblChainShackleConnectionPlates',
    title: 'mooring.minMblChain',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblUsedChainRopeWithKnot',
    title: 'mooring.minMblUsedChainRopeWithKnot',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'MblAnchoring',
    title: 'mooring.minMblAnchoring',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'BottomAnchoringUplift',
    title: 'mooring.bottomAnchoringUplift',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'DepthAtAnchoringPoint',
    title: 'mooring.depthOfAnchoringPoint',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toM,
    inputType: 'float',
  },
  {
    field: 'HoldingPowerAnchoring',
    title: 'mooring.holdingPowerAnchoring',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toKg,
    inputType: 'float',
  },
  {
    field: 'LengthOfBottomRope',
    title: 'mooring.lengthOfBottomChain',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toM,
    inputType: 'float',
  },
  {
    field: 'LengthOfRope',
    title: 'mooring.lengthOfRope',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toM,
    inputType: 'float',
  },
  {
    field: 'TotalLength',
    title: 'mooring.totalLengthByAnchoring',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    transform: toM,
    inputType: 'float',
  },
  {
    field: 'Slope',
    title: 'mooring.incline',
    helpText: 'mooring.slope_helpText',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    inputType: 'float',
  },
  {
    field: 'StopPositionLat',
    title: 'mooring.anchorLineStopN',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    inputType: 'gps',
  },
  {
    field: 'StopPositionLong',
    title: 'mooring.anchorLineStopE',
    selected: true,
    visible: true,
    filterType: 'TEXT',
    inputType: 'gps',
  },
  {
    field: 'actions',
    title: '',
    selected: true,
    visible: true,
    filterType: 'none',
  },
];
